import { getElementChild, getElements } from '../Utility/Dom'
import TimelineRoad from './TimelineRoad'

type SetActive = (link: HTMLElement | null | undefined) => void

class Timeline {
  navs: NodeListOf<HTMLElement>
  tracks: NodeListOf<HTMLElement>
  #scrollRatio = 0.8

  #scrollTolerance = 1
  #spyMargin = '-40% 0px -40% 0px'

  constructor () {
    this.navs = getElements('timeline-nav')
    this.tracks = document.querySelectorAll<HTMLElement>('[data-timeline-track]')
  }

  init (): void {
    this.navs.forEach((nav) => this.#initNav(nav))
    this.tracks.forEach((track) => new TimelineRoad(track).init())
  }

  #centerLink (list: HTMLElement, link: HTMLElement): void {
    const listRect = list.getBoundingClientRect()
    const linkRect = link.getBoundingClientRect()
    const offset = (linkRect.left + linkRect.width / 2) - (listRect.left + listRect.width / 2)

    list.scrollBy({ behavior: 'smooth', left: offset })
  }

  #initActiveLink (nav: HTMLElement, list: HTMLElement): void {
    const links = Array.from(list.querySelectorAll<HTMLElement>('[data-timeline-nav-link]'))
    const linkByUid = new Map(
      links.map((link): [string | undefined, HTMLElement] => [link.dataset.timelineNavLink, link])
    )

    let activeLink: HTMLElement | null = null
    const setActive: SetActive = (link) => {
      if (!link || link === activeLink) return
      activeLink = link

      links.forEach((other) => {
        other.classList.toggle('timeline__nav-link--active', other === link)
      })
      this.#centerLink(list, link)
    }
    links.forEach((link) => {
      link.addEventListener('click', () => setActive(link))
    })

    this.#initScrollSpy(nav, linkByUid, setActive)
  }

  #initNav (nav: HTMLElement): void {
    const list = getElementChild(nav, 'timeline-nav-list')

    if (!list) return

    const prev = getElementChild(nav, 'timeline-nav-prev')
    const next = getElementChild(nav, 'timeline-nav-next')
    const update = (): void => this.#update(nav, list, prev, next)
    const refresh = (): void => {
      update()
      this.#syncNavHeight(nav)
    }

    list.addEventListener('scroll', update, { passive: true })
    window.addEventListener('resize', refresh)

    if ('ResizeObserver' in window) {
      new window.ResizeObserver(refresh).observe(nav)
    }

    if (prev) {
      prev.addEventListener('click', () => this.#step(list, -1))
    }

    if (next) {
      next.addEventListener('click', () => this.#step(list, 1))
    }

    this.#initActiveLink(nav, list)
    refresh()
  }

  #initScrollSpy (nav: HTMLElement, linkByUid: Map<string | undefined, HTMLElement>, setActive: SetActive): void {
    const timeline = nav.closest('.timeline')
    const items = timeline ? Array.from(timeline.querySelectorAll<HTMLElement>('[data-timeline-item]')) : []

    if (!items.length || !('IntersectionObserver' in window)) return

    const visible = new Set<HTMLElement>()
    const observer = new window.IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) visible.add(entry.target as HTMLElement)
        else visible.delete(entry.target as HTMLElement)
      })

      if (!visible.size) return

      const current = Array.from(visible).reduce((top, el) =>
        el.getBoundingClientRect().top < top.getBoundingClientRect().top ? el : top
      )

      setActive(linkByUid.get(current.dataset.timelineItem))
    }, { rootMargin: this.#spyMargin, threshold: 0 })

    items.forEach((item) => observer.observe(item))
  }

  #step (list: HTMLElement, direction: number): void {
    list.scrollBy({
      behavior: 'smooth',
      left: direction * list.clientWidth * this.#scrollRatio
    })
  }

  #syncNavHeight (nav: HTMLElement): void {
    const timeline = nav.closest<HTMLElement>('.timeline')
    if (timeline) {
      timeline.style.setProperty('--tl-nav-h', `${nav.offsetHeight}px`)
    }
  }

  #update (nav: HTMLElement, list: HTMLElement, prev: HTMLElement | null, next: HTMLElement | null): void {
    const maxScroll = list.scrollWidth - list.clientWidth
    const canScroll = maxScroll > this.#scrollTolerance
    const fadePrev = canScroll && list.scrollLeft > this.#scrollTolerance
    const fadeNext = canScroll && list.scrollLeft < maxScroll - this.#scrollTolerance

    nav.toggleAttribute('data-fade-prev', fadePrev)
    nav.toggleAttribute('data-fade-next', fadeNext)

    if (prev) prev.hidden = !fadePrev
    if (next) next.hidden = !fadeNext
  }
}

const timeline = new Timeline()
timeline.init()

export default Timeline
